import React, { useMemo } from 'react';

const CHART_WIDTH = 300;
const CHART_HEIGHT = 120;
const PADDING = { top: 10, right: 15, bottom: 30, left: 40 };

export function RetentionTaxonomyChart({ history, color }) {
  const chartData = useMemo(() => {
    if (!history || history.length === 0) {
      return { path: '', points: [], yLabels: [], xLabels: [] };
    }

    const innerWidth = CHART_WIDTH - PADDING.left - PADDING.right;
    const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;

    // Calculate scales
    const xStep = innerWidth / Math.max(history.length - 1, 1);
    const minY = 0;
    const maxY = 100;
    const yScale = (value) => innerHeight - ((value - minY) / (maxY - minY)) * innerHeight;

    // Generate path and points
    const points = history.map((item, index) => ({
      x: PADDING.left + index * xStep,
      y: PADDING.top + yScale(item.retention),
      retention: item.retention,
      date: item.date
    }));

    // Create SVG path
    let path = '';
    points.forEach((point, index) => {
      if (index === 0) {
        path += `M ${point.x} ${point.y}`;
      } else {
        // Smooth curve using quadratic bezier
        const prev = points[index - 1];
        const cpX = (prev.x + point.x) / 2;
        path += ` Q ${cpX} ${prev.y}, ${point.x} ${point.y}`;
      }
    });

    // Y axis labels
    const yLabels = [
      { value: 100, y: PADDING.top + yScale(100) },
      { value: 50, y: PADDING.top + yScale(50) },
      { value: 0, y: PADDING.top + yScale(0) }
    ];

    // X axis labels - show dates at regular intervals
    const xLabels = [];
    if (history.length > 0) {
      const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
      };

      // Show up to 4 labels evenly distributed
      const maxLabels = Math.min(4, history.length);
      const step = Math.max(1, Math.floor((history.length - 1) / (maxLabels - 1)) || 1);

      for (let i = 0; i < history.length; i += step) {
        if (xLabels.length < maxLabels) {
          xLabels.push({ label: formatDate(history[i].date), x: points[i].x });
        }
      }

      // Always include the last point if not already included
      if (history.length > 1 && xLabels[xLabels.length - 1]?.x !== points[points.length - 1].x) {
        xLabels.push({ label: formatDate(history[history.length - 1].date), x: points[points.length - 1].x });
      }
    }

    return { path, points, yLabels, xLabels };
  }, [history]);

  if (!history || history.length === 0) {
    return (
      <div className="taxonomy-chart taxonomy-chart--empty">
        <p>Sem dados históricos</p>
      </div>
    );
  }

  return (
    <div className="taxonomy-chart">
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="taxonomy-chart__svg"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid lines */}
        {chartData.yLabels.map((label, i) => (
          <line
            key={`grid-${i}`}
            x1={PADDING.left}
            y1={label.y}
            x2={CHART_WIDTH - PADDING.right}
            y2={label.y}
            className="taxonomy-chart__grid-line"
          />
        ))}

        {/* Y axis labels */}
        {chartData.yLabels.map((label, i) => (
          <text
            key={`y-label-${i}`}
            x={PADDING.left - 8}
            y={label.y}
            className="taxonomy-chart__y-label"
          >
            {label.value}%
          </text>
        ))}

        {/* X axis labels */}
        {chartData.xLabels.map((label, i) => (
          <text
            key={`x-label-${i}`}
            x={label.x}
            y={CHART_HEIGHT - PADDING.bottom + 15}
            className="taxonomy-chart__x-label"
          >
            {label.label}
          </text>
        ))}

        {/* Area under the line */}
        <path
          d={`${chartData.path} L ${chartData.points[chartData.points.length - 1]?.x} ${CHART_HEIGHT - PADDING.bottom} L ${chartData.points[0]?.x} ${CHART_HEIGHT - PADDING.bottom} Z`}
          className="taxonomy-chart__area"
          style={{ fill: color, opacity: 0.1 }}
        />

        {/* Line */}
        <path
          d={chartData.path}
          className="taxonomy-chart__line"
          style={{ stroke: color }}
        />

        {/* Points */}
        {chartData.points.map((point, i) => (
          <circle
            key={`point-${i}`}
            cx={point.x}
            cy={point.y}
            r="3"
            className="taxonomy-chart__point"
            style={{ fill: color }}
          />
        ))}
      </svg>
    </div>
  );
}
